import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button,
  FormGroup, Label, Input
} from 'reactstrap';
import { supabase } from '../../supabaseClient';
import {
  FiUploadCloud, FiTrash2, FiImage, FiPlus
} from 'react-icons/fi';

const STORAGE_BUCKET = 'hero';

const HeroManager = () => {
  const [heroImages, setHeroImages]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(false);
  const [deleteModal, setDeleteModal]   = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [file, setFile]                 = useState(null);
  const [preview, setPreview]           = useState('');
  const [title, setTitle]               = useState('');
  const [uploading, setUploading]       = useState(false);
  const [hoveredId, setHoveredId]       = useState(null);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const fileRef = useRef();

  const fetchHeroImages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('hero_section')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setHeroImages(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHeroImages(); }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select an image');
      return;
    }

    if (heroImages.length >= 5) {
      setError('Maximum of 5 hero images allowed. Please delete some images first.');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from(STORAGE_BUCKET)
        .getPublicUrl(fileName);

      const { error: dbError } = await supabase
        .from('hero_section')
        .insert([
          {
            title: title || '',
            image_url: data.publicUrl
          }
        ]);

      if (dbError) throw dbError;

      setSuccess('Hero image added successfully!');
      setModal(false);
      setFile(null);
      setPreview('');
      setTitle('');
      fetchHeroImages();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('hero_section')
        .delete()
        .eq('id', selectedItem.id);

      if (error) throw error;

      setSuccess('Hero image deleted successfully!');
      setDeleteModal(false);
      fetchHeroImages();
    } catch (err) {
      setError(err.message);
    }
  };

  const openAdd = () => { setFile(null); setPreview(''); setTitle(''); setError(''); setSuccess(''); setModal(true); };
  const openDel = (img) => { setSelectedItem(img); setDeleteModal(true); };

  return (
    <div style={s.wrap}>
      <div style={s.hdr}>
        <div>
          <h2 style={s.title}><FiImage style={{ marginRight: 10 }} />Hero Section</h2>
          <p style={s.sub}>{heroImages.length}/5 images · Manage your homepage hero banner</p>
        </div>
        <button style={s.addBtn} onClick={openAdd} disabled={heroImages.length >= 5}>
          <FiPlus size={16} /> Add Image
        </button>
      </div>

      {success && (
        <div style={s.successBanner}>✓ {success}</div>
      )}
      {error && (
        <div style={s.errorBanner}>⚠ {error}</div>
      )}

      {loading ? (
        <div style={s.center}><span style={s.spinner} /></div>
      ) : heroImages.length === 0 ? (
        <div style={s.empty}>
          <FiImage size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p>No hero images yet. Click "Add Image" to get started.</p>
        </div>
      ) : (
        <div style={s.grid}>
          {heroImages.map(img => (
            <div
              key={img.id}
              style={s.card}
              onMouseEnter={() => setHoveredId(img.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <img src={img.image_url} alt="Hero" style={s.img} />
              <div style={s.cardTitle}>{img.title}</div>
              <div style={{ ...s.overlay, opacity: hoveredId === img.id ? 1 : 0 }}>
                <button style={{ ...s.iconBtn, ...s.delIconBtn }} onClick={() => openDel(img)} title="Delete">
                  <FiTrash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="md">
        <ModalHeader toggle={() => setModal(false)}>
          <FiUploadCloud style={{ marginRight: 8 }} />Add Hero Image
        </ModalHeader>
        <ModalBody>
          {error && <div style={s.modalErr}>⚠ {error}</div>}
          <FormGroup>
            <Label for="hero-title" style={{ fontWeight: 600 }}>
              Title
            </Label>
            <Input
              id="hero-title"
              type="text"
              placeholder="Enter hero title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              style={{ marginBottom: 16, background: 'rgba(255,255,255,0.05)', border: '1px solid #ccc', }}
            />
          </FormGroup>
          <div style={s.dropZone} onClick={() => fileRef.current.click()}>
            <FiUploadCloud size={36} style={{ opacity: 0.5, marginBottom: 8 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>Click to browse files</p>
            <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.5 }}>
              JPG, PNG, WEBP — Max 5MB
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
          {preview && (
            <img src={preview} alt="Preview" style={{ width: '100%', borderRadius: 10, marginTop: 12, maxHeight: 200, objectFit: 'cover' }} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)}>Cancel</Button>
          <Button
            color="primary"
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{ background: 'linear-gradient(135deg,#7c3aed,#ec4899)', border: 'none' }}
          >
            {uploading ? 'Uploading…' : 'Add Image'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered size="sm">
        <ModalHeader toggle={() => setDeleteModal(false)}>
          <FiTrash2 style={{ marginRight: 8, color: '#ef4444' }} />Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p style={{ margin: 0 }}>
            Are you sure you want to permanently delete this hero image? This cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDeleteModal(false)}>Cancel</Button>
          <Button color="danger" onClick={handleDelete}>Delete Image</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const s = {
  wrap: { padding: '8px 0' },
  hdr:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center' },
  sub:   { margin: '4px 0 0', opacity: 0.5, fontSize: 13 },
  addBtn: {
    background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
    color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14,
    display: 'flex', alignItems: 'center', gap: 6,
    boxShadow: '0 4px 16px rgba(124,58,237,0.35)',
    transition: 'transform 0.15s',
  },
  successBanner: {
    background: '#10b981',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  errorBanner: {
    background: '#ef4444',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 14,
  },
  modalErr: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: 8, padding: '8px 12px',
    color: '#ef4444', fontSize: 13, marginBottom: 14,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
  },
  card: {
    borderRadius: 14, overflow: 'hidden', position: 'relative',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    aspectRatio: '16/9',
    cursor: 'pointer',
  },
  cardTitle: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(0,0,0,0.7)',
    color: '#fff',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 600,
  },
  img: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  overlay: {
    position: 'absolute', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 10, opacity: 0, transition: 'opacity 0.2s',
  },
  iconBtn: {
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)',
    color: '#fff', borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
    backdropFilter: 'blur(8px)', transition: 'background 0.15s',
  },
  delIconBtn: { background: 'rgba(239,68,68,0.3)', borderColor: 'rgba(239,68,68,0.4)' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: {
    width: 36, height: 36,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#7c3aed', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },
  empty: { textAlign: 'center', padding: '60px 20px', opacity: 0.5 },
  dropZone: {
    border: '2px dashed rgba(124,58,237,0.5)',
    borderRadius: 12, padding: '32px 20px',
    textAlign: 'center', cursor: 'pointer',
    transition: 'border-color 0.2s, background 0.2s',
    background: 'rgba(124,58,237,0.05)',
  },
};

export default HeroManager;
